import React, { useMemo, useState } from 'react';
import { StylesConfig, ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../App.types';
import * as yup from 'yup';
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import Button from '../../../../ui/Buttons/Button/Button';
import styles from './ArtistFromSpotifyForm.module.css';
import { getArtists, usePaginatedFetch } from '@round/api';
import debounce from 'lodash/debounce';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import Select from '../../../../ui/DataEntry/Select/Select';

type ArtistFromSpotifyFormValues = {
    url: string;
    artist: ValueType<GenericDropdownOption<number>, false>;
};

export type ArtistFromSpotifyFormProps = {
    handleSubmit: (values: ArtistFromSpotifyFormValues, helpers: FormikHelpers<ArtistFromSpotifyFormValues>) => void;
    initialValues?: ArtistFromSpotifyFormValues;
    renderActions?: (props: FormikProps<ArtistFromSpotifyFormValues>) => React.ReactNode;
    disabledFields?: (keyof ArtistFromSpotifyFormValues)[];
};

const validationSchema = yup.object({
    url: yup.string().required().url('Enter valid url'),
});

const defaultInitialValues: ArtistFromSpotifyFormProps['initialValues'] = {
    url: '',
    artist: null,
};

const defaultRenderActions: ArtistFromSpotifyFormProps['renderActions'] = ({ submitForm, isSubmitting, isValid }) => (
    <Button
        className={styles.submitButton}
        type="filled"
        color="black"
        onClick={submitForm}
        loading={isSubmitting}
        disabled={!isValid}
    >
        Submit
    </Button>
);

// const created to keep the same instance of an array, because it is used
// in the dependency list. If passed as default value with array literal
// it would end up on infinite update.
const defaultDisabledFields: ArtistFromSpotifyFormProps['disabledFields'] = [];

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    valueContainer: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: state.isMulti ? '0.625rem 1rem 0.4375rem 1rem' : '0.625rem 1rem',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '0 3px 3px 0',
    }),
    multiValueRemove: (base) => ({
        ...base,
        height: '1rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1000,
    }),
};

const ArtistFromSpotifyForm = ({
    handleSubmit,
    initialValues = defaultInitialValues,
    renderActions = defaultRenderActions,
    disabledFields = defaultDisabledFields,
}: ArtistFromSpotifyFormProps) => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const {
        data: artists,
        loading: artistsLoading,
        getCurrentPage,
        hasNextPage: hasArtistsNextPage,
    } = usePaginatedFetch(getArtists, { page, search, page_size: 10 });
    const getArtistsCurrentPage = useMemo(() => debounce(getCurrentPage, 700), [getCurrentPage]);

    useAbortableEffect(
        (signal) => {
            if (Array.isArray(disabledFields) && !disabledFields.includes('artist')) {
                getArtistsCurrentPage({ signal });
            }
        },
        [getArtistsCurrentPage, disabledFields]
    );

    const artistOptions: GenericDropdownOption<number>[] = useMemo(
        () => artists.map((a) => ({ value: a.id, label: a.name })),
        [artists]
    );

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {(props) => (
                <>
                    <Form>
                        <FormField>
                            <Label htmlFor="url">Spotify URL</Label>
                            <Field id="url" name="url" placeholder="Enter Spotify URL" />
                            <ErrorMessage name="url">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>
                        <FormField>
                            <Label htmlFor="artist">Artist</Label>
                            <Field name="artist">
                                {(props: FieldProps) => (
                                    <Select
                                        styles={selectStyles}
                                        isLoading={artistsLoading}
                                        isDisabled={Boolean(
                                            disabledFields?.includes(
                                                props.field.name as keyof ArtistFromSpotifyFormProps['initialValues']
                                            )
                                        )}
                                        inputValue={search}
                                        onInputChange={(value) => {
                                            setSearch(value);
                                            setPage(1);
                                        }}
                                        options={artistOptions}
                                        placeholder="Select artist to link"
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                                        onMenuScrollToBottom={() => {
                                            if (hasArtistsNextPage) {
                                                setPage((page) => page + 1);
                                            }
                                        }}
                                    />
                                )}
                            </Field>
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};

export default ArtistFromSpotifyForm;
